import { useMutation, useQueryClient } from 'react-query'
import { message, UploadFile } from 'antd'
import { postJSON } from 'common/services/api'
import { ReferralCreate, ReferralResponse } from '@lmc/validations'
import { REFERRALS_CATEGORY_ID } from '../constants'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'

type CreateReferralInput = ReferralCreate
type Referral = ReferralResponse

interface UseReferralCreateReturn {
  createReferral: (
    input: CreateReferralInput,
    fileList?: UploadFile[]
  ) => Promise<Referral>
  isCreating: boolean
}

export const useReferralCreate = (
  onSuccess?: () => void
): UseReferralCreateReturn => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      input,
      fileList
    }: {
      input: CreateReferralInput
      fileList?: UploadFile[]
    }) => {
      const hasFiles = fileList && fileList.length > 0

      let attachmentIds: string[] = []

      // Upload documents using presign/commit flow if files are provided
      if (hasFiles) {
        try {
          attachmentIds = await uploadDocuments(
            fileList,
            input.resident,
            REFERRALS_CATEGORY_ID
          )
        } catch (error) {
          throw new Error('Failed to upload attachments')
        }
      }

      const formattedInput = {
        ...input,
        ...(attachmentIds.length > 0 && { attachments: attachmentIds })
      }

      const response = await postJSON({
        url: '/api/list/referrals',
        method: 'POST',
        json: formattedInput
      })

      return response.result as Referral
    },
    {
      onSuccess: (data) => {
        message.success('Referral created successfully')
        // Invalidate referrals list to refetch
        queryClient.invalidateQueries(['referrals', data.resident])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to create referral')
      }
    }
  )

  const createReferral = async (
    input: CreateReferralInput,
    fileList?: UploadFile[]
  ): Promise<Referral> => {
    return mutation.mutateAsync({ input, fileList })
  }

  return {
    createReferral,
    isCreating: mutation.isLoading
  }
}
